import { useUserContext } from '@basqet-dashboard/common/context';
import { usePostHog } from 'posthog-js/react';
import { useCallback, useEffect, useRef } from 'react';

/**
 * Custom hook for PostHog analytics tracking.
 * Provides a consistent interface for tracking events across the app.
 * Automatically includes { platform: 'web', merchant_id } in every event.
 *
 * @returns {{ track: (eventName: string, properties?: object) => void }}
 */
export const useAnalytics = () => {
  const posthog = usePostHog();
  const { user } = useUserContext();
  const merchantId = user?.merchantId;

  const track = useCallback(
    (eventName, properties = {}) => {
      posthog?.capture(eventName, {
        platform: 'web',
        merchant_id: merchantId?.toString() ?? '',
        ...properties
      });
    },
    [posthog, merchantId]
  );

  return { track };
};

/**
 * Track a page-view event exactly once on component mount
 * @param {string} eventName - The analytics event name
 * @param {object} [properties] - Additional event properties
 */
export const useTrackOnMount = (eventName, properties) => {
  const { track } = useAnalytics();
  const hasTracked = useRef(false);

  useEffect(() => {
    /* istanbul ignore next -- defensive guard; empty deps fires once */
    if (!hasTracked.current) {
      track(eventName, properties);
      hasTracked.current = true;
    }
  }, []); // intentionally empty — fire once on mount only
};
